/**
 * تمِ سایت.
 *
 * چطور کار می‌کنه: `themeCss()` از `@palsandplans/tokens` یه استایل‌شیت می‌سازه که
 * همه‌ی رنگ‌ها رو به شکلِ «متغیرِ CSS» تعریف می‌کنه (`--bg`, `--brand`, …).
 * بقیه‌ی سایت هیچ‌جا کدِ رنگ نمی‌نویسه، فقط `var(--bg)` صدا می‌زنه. پس
 * عوض‌کردنِ تم = عوض‌کردنِ یک صفت روی تگِ <html>، بدونِ رندرِ دوباره‌ی React.
 *
 * چرا از خودِ core (`lib/theme`) استفاده می‌کنیم و یه سوییچِ جدا نمی‌نویسیم:
 * تصمیمِ «کدوم تم روشنه» و کلیدِ ذخیره‌سازیش باید یکی باشه. اسم‌ها هم همون
 * night/coralِ اپ می‌مونن؛ سایت فقط برای CSS به dark/light ترجمه‌شون می‌کنه.
 */
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { themeCss } from '@palsandplans/tokens';
import { getTheme, initTheme, setTheme, type ThemeName } from '@/core/lib/theme';

const STYLE_ID = 'goout-tokens';

/** استایل‌شیتِ توکن‌ها یک‌بار به <head> اضافه می‌شه. */
function ensureTokenStyles(): void {
  if (document.getElementById(STYLE_ID)) return;
  const el = document.createElement('style');
  el.id = STYLE_ID;
  el.textContent = themeCss();
  document.head.appendChild(el);
}

const toAttr = (name: ThemeName) => (name === 'night' ? 'dark' : 'light');

interface ThemeState {
  name: ThemeName;
  isDark: boolean;
  toggle: () => void;
}

const Ctx = createContext<ThemeState>({ name: 'night', isDark: true, toggle: () => undefined });
export const useTheme = () => useContext(Ctx);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [name, setName] = useState<ThemeName>(getTheme());
  // تا وقتی نمی‌دونیم کاربر چی ذخیره کرده، رنگِ اشتباه نشون ندیم؛ خواندنِ
  // localStorage از پشتِ پورت async است، پس یک تیکِ کوتاه طول می‌کشه.
  const [ready, setReady] = useState(false);

  useEffect(() => {
    ensureTokenStyles();
    const prefersDark = window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
    initTheme(prefersDark).then(() => {
      setName(getTheme());
      setReady(true);
    });
  }, []);

  // صفتِ روی <html> تنها جاییه که تم رو به CSS می‌رسونه.
  useEffect(() => {
    document.documentElement.dataset.theme = toAttr(name);
  }, [name]);

  const toggle = useCallback(() => {
    const next: ThemeName = getTheme() === 'night' ? 'coral' : 'night';
    setTheme(next);
    setName(next);
  }, []);

  if (!ready) return null;
  return (
    <Ctx.Provider value={{ name, isDark: name === 'night', toggle }}>
      {children}
    </Ctx.Provider>
  );
}
